import { useCallback, useEffect, useRef, useState } from 'react';
import { getSpallaSubtitlesPtBr } from 'services/spallaApi';
import { SpallaSubtitles } from 'types';

function findCueIdx(cues: SpallaSubtitles, t: number): number {
  let lo = 0,
    hi = cues.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1,
      c = cues[mid];
    if (t < c.inicio_numerico) hi = mid - 1;
    else if (t > c.fim_numerico) lo = mid + 1;
    else return mid;
  }
  return -1;
}

export function usePtBrSubtitles(videoId: string, language: string) {
  const [cues, setCues] = useState<SpallaSubtitles>([]);
  const [text, setText] = useState('');
  const lastIdx = useRef(-1);
  const lastTick = useRef(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getSpallaSubtitlesPtBr(videoId, language);
        if (alive) {
          setCues(data);
          setText('');
          lastIdx.current = -1;
        }
      } catch {
        if (alive) {
          setCues([]);
          setText('');
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [videoId, language]);

  const onProgress = useCallback(
    (currentTime: number) => {
      const now = Date.now();
      if (now - lastTick.current < 180) return;
      lastTick.current = now;

      if (!cues.length) {
        if (text) setText('');
        lastIdx.current = -1;
        return;
      }

      const i = lastIdx.current;
      if (i >= 0) {
        const c = cues[i];
        if (currentTime >= c.inicio_numerico && currentTime <= c.fim_numerico) return;
      }
      const idx = findCueIdx(cues, currentTime);
      lastIdx.current = idx;
      setText(idx >= 0 ? cues[idx].texto : '');
    },
    [cues, text]
  );

  return { text, hasSubtitles: cues.length > 0, onProgress };
}
